<template>
  <div class="page-container">
    <el-row :gutter="16" v-loading="loading">
      <el-col
        v-for="group in configGroups"
        :key="group.group"
        :xs="24" :sm="24" :md="12" :lg="12"
        style="margin-bottom:16px"
      >
        <el-card shadow="never">
          <div slot="header" class="card-header">
            <span class="group-title">{{ group.label }}</span>
            <el-button
              v-permission="['system:config:update']"
              type="primary"
              size="mini"
              :loading="savingGroup === group.group"
              @click="handleSave(group)"
            >保存</el-button>
          </div>

          <el-form
            v-if="formData[group.group]"
            :ref="`form_${group.group}`"
            :model="formData[group.group]"
            label-width="130px"
            size="small"
          >
            <el-form-item
              v-for="item in group.items"
              :key="item.key"
              :label="item.label"
            >
              <el-input
                v-if="item.type === 'textarea'"
                v-model="formData[group.group][item.key]"
                type="textarea"
                :rows="3"
                :placeholder="item.remark || '请输入'"
              />
              <el-input-number
                v-else-if="item.type === 'number'"
                v-model.number="formData[group.group][item.key]"
                :min="0"
                style="width:160px"
              />
              <el-switch
                v-else-if="item.type === 'boolean'"
                v-model="formData[group.group][item.key]"
                active-value="1"
                inactive-value="0"
              />
              <div v-else-if="item.type === 'image'" class="image-list">
                <div v-if="formData[group.group][item.key]" class="image-box">
                  <el-image
                    :src="formData[group.group][item.key]"
                    fit="cover"
                    class="image-inner"
                    :preview-src-list="[formData[group.group][item.key]]"
                  />
                  <div class="image-mask">
                    <i class="el-icon-delete" @click="clearImage(group.group, item.key)" />
                  </div>
                </div>
                <el-upload
                  v-if="!formData[group.group][item.key]"
                  action="#"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  :show-file-list="false"
                  :http-request="({ file }) => handleImageUpload(group.group, item.key, file)"
                >
                  <div class="image-add-box">
                    <i :class="isImageUploading(group.group, item.key) ? 'el-icon-loading' : 'el-icon-plus'" />
                  </div>
                </el-upload>
              </div>
              <el-input
                v-else
                v-model="formData[group.group][item.key]"
                :placeholder="item.remark || '请输入'"
                clearable
              />
              <div v-if="item.remark" class="config-remark">{{ item.remark }}</div>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getConfigList, updateConfig, uploadConfigImage } from '@/api/system-config'

export default {
  name: 'SystemConfig',
  data() {
    return {
      loading: false,
      savingGroup: '',
      configGroups: [],
      formData: {},
      imageUploading: {}
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const res = await getConfigList()
        this.configGroups = Array.isArray(res.data) ? res.data : []
        this.initFormData()
      } catch (e) {
        this.configGroups = []
        this.$message.error('加载配置失败，请刷新重试')
      } finally {
        this.loading = false
      }
    },
    initFormData() {
      const data = {}
      this.configGroups.forEach(group => {
        data[group.group] = {}
        group.items.forEach(item => {
          data[group.group][item.key] = item.value ?? ''
        })
      })
      this.formData = data
    },
    imageUploadKey(group, key) {
      return `${group}_${key}`
    },
    isImageUploading(group, key) {
      return !!this.imageUploading[this.imageUploadKey(group, key)]
    },
    clearImage(group, key) {
      this.$set(this.formData[group], key, '')
    },
    async handleImageUpload(group, key, file) {
      const uploadKey = this.imageUploadKey(group, key)
      this.$set(this.imageUploading, uploadKey, true)
      try {
        const res = await uploadConfigImage(file)
        this.$set(this.formData[group], key, res.data.url)
        this.$message.success('图片上传成功')
      } catch (e) {
        this.$message.error(e.message || '上传失败')
      } finally {
        this.$set(this.imageUploading, uploadKey, false)
      }
    },
    async handleSave(group) {
      const updates = group.items.map(item => ({
        key: item.key,
        value: String(this.formData[group.group][item.key] ?? '')
      }))
      this.savingGroup = group.group
      try {
        await updateConfig(updates)
        this.$message.success('保存成功')
      } finally {
        this.savingGroup = ''
      }
    }
  }
}
</script>

<style scoped>
.page-container { padding: 16px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.group-title { font-weight: 600; font-size: 14px; }
.config-remark { font-size: 12px; color: #909399; line-height: 1.4; margin-top: 4px; }
.image-list { display: flex; flex-wrap: wrap; gap: 8px; }
.image-box {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #dcdfe6;
}
.image-inner { width: 100%; height: 100%; display: block; }
.image-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
}
.image-box:hover .image-mask { opacity: 1; }
.image-add-box {
  width: 120px;
  height: 120px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #8c939d;
  cursor: pointer;
  transition: border-color 0.2s;
}
.image-add-box:hover { border-color: #409eff; color: #409eff; }
</style>
